import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Spotlight, cn } from '@mypoz/ui';

import type { TourId, TourTarget } from '@/features/tour/types.js';
import { useTourSteps } from '@/features/tour/useTourSteps.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';

/** The sticky header's height (`h-16` in `Layout`): a page target under it is scrolled clear. */
const HEADER_HEIGHT = 64;

export interface TourOverlayProps {
  tour: TourId;
  index: number;
  customerId: string;
  name?: string;
  onGo: (index: number) => void;
  onFinish: () => void;
}

interface Resolved {
  readonly element: HTMLElement | null;
  /** The text for the target that was found, when it differs from the step's own. */
  readonly body?: string;
}

/**
 * The first of `targets` that is on screen. `display: none` — the desktop sidebar on a phone — has
 * no boxes, so it is passed over for the next choice.
 */
function resolveTarget(targets: readonly TourTarget[]): Resolved {
  for (const target of targets) {
    const found = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${target.id}"]`));
    const element = found.find((candidate) => candidate.getClientRects().length > 0);
    if (element) return { element, ...(target.body ? { body: target.body } : {}) };
  }
  return { element: null };
}

/**
 * One step of a guided tour (MYP-83): a small popover pointing at the step's element, the rest of
 * the page dimmed — `Spotlight` — with Skip, Back and Next/Done.
 */
export function TourOverlay({ tour, index, customerId, name, onGo, onFinish }: TourOverlayProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const nextRef = useRef<HTMLButtonElement>(null);
  const steps = useTourSteps(tour, { customerId, ...(name ? { name } : {}) });
  const step = steps[index] ?? steps[0];
  const [resolved, setResolved] = useState<Resolved>({ element: null });

  /*
    The step's targets, read by the effect below through a ref: the array is rebuilt on every render
    (the text is translated afresh), so it cannot be a dependency itself. `targetKey` — the ids —
    is what says the step's targets actually changed.
  */
  const targetKey = step?.targets.map((target) => target.id).join(' ') ?? '';
  const targetsRef = useRef(step?.targets);
  targetsRef.current = step?.targets;

  // Looked up after the page has rendered this step (the order steps swap in), before paint.
  useLayoutEffect(() => {
    const targets = targetsRef.current;
    if (!targets || targets.length === 0) {
      setResolved({ element: null });
      return;
    }
    const found = resolveTarget(targets);
    setResolved(found);
    if (found.element) return;
    /*
      Not there yet: a tour that opens with its panel (the Add package form's) can look before the
      panel's fields have laid out. One more look a frame later, before settling on the centred
      fallback — cheap, and it keeps working if the panel ever gains an opening animation.
    */
    const frame = window.requestAnimationFrame(() => setResolved(resolveTarget(targets)));
    return () => window.cancelAnimationFrame(frame);
  }, [tour, index, targetKey]);

  if (!step) return null;

  const total = steps.length;
  const last = index >= total - 1;
  const { action } = step;

  return (
    <Spotlight
      target={resolved.element}
      title={step.title}
      onClose={onFinish}
      focusKey={`${tour}:${index}`}
      initialFocusRef={nextRef}
      // A header target is already in view: the header is sticky.
      insetTop={resolved.element?.closest('header') ? 0 : HEADER_HEIGHT}
    >
      <p className="text-sm text-ink">{resolved.body ?? step.body}</p>

      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-xs text-muted tabular-nums">
          {t('tour.progress', { current: fmt.number(index + 1), total: fmt.number(total) })}
        </p>
        <ol aria-hidden="true" className="flex gap-1.5">
          {steps.map((entry, dot) => (
            <li
              key={`${entry.title}-${dot}`}
              className={cn('size-1.5 rounded-full', dot === index ? 'bg-brand' : 'bg-line')}
            />
          ))}
        </ol>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {!last ? (
          <button type="button" onClick={onFinish} className={buttonClass('ghost', 'mr-auto px-3')}>
            {t('tour.skip')}
          </button>
        ) : null}
        {index > 0 ? (
          <button
            type="button"
            onClick={() => onGo(index - 1)}
            className={buttonClass('secondary', cn('px-3', last && 'mr-auto'))}
          >
            {t('tour.back')}
          </button>
        ) : null}
        {last && action ? (
          <>
            <button type="button" onClick={onFinish} className={buttonClass('secondary', 'px-3')}>
              {t('tour.done')}
            </button>
            <button
              ref={nextRef}
              type="button"
              onClick={() => {
                onFinish();
                navigate(action.to);
              }}
              className={buttonClass('primary', 'px-4')}
            >
              {action.label}
            </button>
          </>
        ) : (
          <button
            ref={nextRef}
            type="button"
            onClick={() => (last ? onFinish() : onGo(index + 1))}
            className={buttonClass('primary', 'px-4')}
          >
            {last ? t('tour.done') : (step.nextLabel ?? t('tour.next'))}
          </button>
        )}
      </div>
    </Spotlight>
  );
}
